import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { getWidgetDefinition } from '../widgetRegistry';
import { Button, Label } from '../../../components/ui';
import TextInput from '../../../components/ui/TextInput';
import { cn } from '../../../lib/utils';

export interface WidgetConfigPanelProps {
  widgetId: string;
  widgetType: string;
  settings: Record<string, unknown>;
  onSave: (settings: Record<string, unknown>) => void;
  onClose: () => void;
}

const WidgetConfigPanel: React.FC<WidgetConfigPanelProps> = ({
  widgetId,
  widgetType,
  settings: initialSettings,
  onSave,
  onClose,
}) => {
  const { colors } = useTheme();
  const def = getWidgetDefinition(widgetType);
  const [settings, setSettings] = useState<Record<string, unknown>>(initialSettings ?? {});

  useEffect(() => {
    const merged = def ? { ...def.defaultSettings, ...initialSettings } : { ...initialSettings };
    setSettings(merged);
  }, [widgetId, widgetType, initialSettings, def]);

  const handleChange = (key: string, value: unknown) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(settings);
    onClose();
  };

  if (!def) {
    return (
      <div className={cn('rounded-xl border p-6', colors.border, colors.surface)}>
        <p className={colors.textMuted}>Unknown widget type.</p>
        <Button type="button" variant="outline" className="mt-4" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  const schema = def.settingsSchema as Record<string, { type?: string; default?: unknown; min?: number; max?: number }> | undefined;
  const hasSchema = schema && Object.keys(schema).length > 0;

  const formatLabel = (key: string) =>
    key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();

  return (
    <form onSubmit={handleSubmit} className={cn('rounded-xl border overflow-hidden max-w-lg', colors.border, colors.surface)}>
      <div className={cn('px-5 py-4 border-b', colors.border, colors.headerBg)}>
        <h3 className={cn('text-base font-semibold', colors.text)}>{def.name}</h3>
        <p className={cn('text-xs mt-0.5', colors.textMuted)}>{def.description}</p>
      </div>

      <div className="p-5 space-y-4">
        {hasSchema && schema ? (
          Object.entries(schema).map(([key, field]) => {
            const value = settings[key] ?? field.default;
            if (field.type === 'number') {
              return (
                <div key={key}>
                  <Label htmlFor={`widget-setting-${key}`}>{formatLabel(key)}</Label>
                  <TextInput
                    id={`widget-setting-${key}`}
                    type="number"
                    value={String(value ?? '')}
                    min={field.min}
                    max={field.max}
                    onChange={(e) => handleChange(key, Number(e.target.value))}
                    className="mt-1.5"
                    showClearButton={false}
                  />
                  {(field.min != null || field.max != null) && (
                    <p className={cn('text-[11px] mt-1', colors.textMuted)}>
                      {field.min != null && field.max != null
                        ? `Range: ${field.min}–${field.max}`
                        : field.min != null
                          ? `Minimum: ${field.min}`
                          : `Maximum: ${field.max}`}
                    </p>
                  )}
                </div>
              );
            }
            return (
              <div key={key}>
                <Label htmlFor={`widget-setting-${key}`}>{formatLabel(key)}</Label>
                <TextInput
                  id={`widget-setting-${key}`}
                  value={String(value ?? '')}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="mt-1.5"
                  showClearButton={false}
                />
              </div>
            );
          })
        ) : (
          <div>
            <Label htmlFor="widget-setting-limit">Items to show</Label>
            <TextInput
              id="widget-setting-limit"
              type="number"
              min={1}
              max={50}
              value={String(settings.limit ?? 5)}
              onChange={(e) => handleChange('limit', Number(e.target.value))}
              className="mt-1.5"
              showClearButton={false}
            />
            <p className={cn('text-[11px] mt-1', colors.textMuted)}>How many rows appear in the list (1–50).</p>
          </div>
        )}
      </div>

      <div className={cn('flex items-center justify-end gap-2 px-5 py-4 border-t', colors.border, colors.headerBg)}>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit">Save changes</Button>
      </div>
    </form>
  );
};

export default WidgetConfigPanel;
